// Question definitions for the SCS "Analysis of Adverse Events" wizard.
//
// Mirrors scsOverallExtentOfExposureQuestions / scsDescriptionOfStudiesQuestions:
// `number` is the stable badge shown in the CSR-style index badge (page order,
// hardcoded — it does not renumber as conditional sections toggle), `text` is
// the fallback wording and `tooltip` holds the ticket INFORMATION/helper text
// (the endpoint returns wording only, no tooltip map).
//
// At runtime the wording is overridden by
// GET /clinical_summaries/safety_summary/questions. Every answer on this page
// is saved under its own qAE* attribute, so `textKeys` lists the attributes to
// read the served wording from: the endpoint carries these questions under the
// Clinical Laboratory Evaluations (qLab*) and Description of Studies names, and
// the comparison questions (Q4–Q6) are the Description of Studies questions
// repeated per grouping, where only the badge number changes.

export const SCS_AAE_QUESTIONS = {
  qAETypes: {
    number: 1,
    required: true,
    text: "Which AEs do you want to report?",
  },
  qAEAdditionalGroupingName: {
    number: 2,
    textKeys: ["qLabAdditionalGroupingName"],
    text: "Which additional groupings do you want to compare? Name the grouping and then select the studies in this grouping.",
  },
  qAEComparisonType: {
    number: 3,
    textKeys: ["qLabComparisonType", "qComparisonType"],
    text: "How is the grouping compared?",
  },
  // Q4 / Q5 below are the Description of Studies branch questions (its Q8 / Q9)
  // shown for the comparison type picked in Q3.
  qAENonComparativeGroupNames: {
    number: 4,
    textKeys: ["qNonComparativeGroupNames"],
    text: "What are the actual names used to describe the treatment/analysis groups in the column headers?",
  },
  qAETreatmentGroups: {
    number: 4,
    textKeys: ["qTreatmentGroupNames"],
    text: "What are the actual names used to describe the treatment/analysis groups in the column headers? Please define the role for each column name.",
  },
  qAEComparisonPairs: {
    number: 5,
    textKeys: ["qComparisonPairs"],
    text: "Which dose/treatment groups should be compared?",
  },
  qAEDoseGroups: {
    number: 4,
    textKeys: ["qDoseGroups"],
    text: "Please list the dose group column headers from lowest to highest, starting with Placebo if present.",
  },
};

// The AE categories offered by Q1. `label` is both the multi-select option and
// the wording saved as the answer; `key` is the category's stable identifier
// (one per adverse-events transformer).
//
// TODO: the questions endpoint carries no wording for these yet, so the labels
// are hardcoded here — swap them for the served wording once it is available.
export const SCS_AE_TYPE_OPTIONS = [
  { key: "safety_summary", label: "Overall AEs" },
  { key: "severity", label: "AEs by Severity" },
  { key: "treatment_related", label: "TRAEs" },
  { key: "discontinuation", label: "Discontinuation AEs" },
  { key: "deaths", label: "Deaths" },
  { key: "serious", label: "SAEs" },
  { key: "special_interest", label: "AESI" },
  { key: "other_significant", label: "Other Significant" },
  { key: "frequency", label: "Frequency" },
];

// Comparison type (Q3) -> the value it is saved as. The question is answered
// with the on-screen wording (the same button group as Q7 of the Description of
// Studies page) but persisted as the backend's grouping kind, which also decides
// which branch answers a grouping carries:
//   Non-Comparative -> single_arm    qAETreatmentGroups (plain names)
//   Comparative     -> multi_arm     qAETreatmentGroups ({ name, role })
//                                    + qAEComparisonPairs
//   Dose-Finding    -> dose_finding  qAEDoseGroups
export const SCS_AAE_COMPARISON_TYPE_VALUES = {
  "Non-Comparative": "single_arm",
  Comparative: "multi_arm",
  "Dose-Finding": "dose_finding",
};

// Saved value -> the on-screen wording, for re-populating Q3 from saved data.
export const SCS_AAE_COMPARISON_TYPE_LABELS = Object.fromEntries(
  Object.entries(SCS_AAE_COMPARISON_TYPE_VALUES).map(([label, value]) => [
    value,
    label,
  ])
);
